import React, { ReactElement, useEffect, useState } from 'react'
import Modal from './Modal'
import { IconButton, PrimaryButton } from './Button'
import { InputLabel, TextInput, TextInputWithLabel } from './Input'
import { observer } from 'mobx-react'
import { RoleSelector } from './RoleSelector'
import Radom, { IPermissionRole, TeamInvite } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { usePermissions } from '../util/permissions'
import Copy from '../icons/Copy'
import { errorToast, infoToast, successToast } from '../util/Util'

interface IProps {
  visible: boolean
  isEditing: boolean
  userForm: ICreateUser
  onClose: () => void
  onRoleAssignment: () => void
  onUserFormUpdate: (u: Partial<ICreateUser>) => void
}

export interface ICreateUser {
  roles: IPermissionRole[]
  emailAddress?: string
}

const UserInviteModal = observer((props: IProps): ReactElement => {
  const { has } = usePermissions()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [apiRoles, setAPIRoles] = useState([] as IPermissionRole[])
  const [createdInvite, setCreatedInvite] = useState<TeamInvite | undefined>(undefined)

  const listPermissionRoles = (): void => {
    if (!Radom.isLoggedIn || !props.visible || !has('ReadRoles')) return
    Radom.listPermissionRoles()
      .then(roles => setAPIRoles(roles))
  }

  useEffect(() => {
    listPermissionRoles()
  }, [Radom.isLoggedIn, props.visible])

  const handleClose = (): void => {
    setCreatedInvite(undefined)
    props.onClose()
  }

  const onSubmitUser = async (): Promise<void> => {
    setIsSubmitting(true)
    try {
      const invite = await Radom.inviteMember({
        roles: props.userForm.roles.map(r => r.id),
        emailAddress: props.userForm.emailAddress && props.userForm.emailAddress.length > 0
          ? props.userForm.emailAddress.toLowerCase()
          : undefined
      })

      successToast(`Successfully ${props.isEditing ? 'updated' : 'invited'} user`)
      if (props.isEditing) {
        handleClose()
      } else {
        setCreatedInvite(invite)
      }
    } catch (err) {
      errorToast(err.reason || err.response?.data?.error || err.message)
      console.error('Failed to assign user to role', err)
    }
    setIsSubmitting(false)
  }

  return <Modal
    visible={props.visible}
    onClose={handleClose}
    title={props.isEditing ? 'Update user' : createdInvite ? 'Invite sent' : 'Invite team member'}>
    <form
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: createdInvite ? 'auto' : '300px',
        justifyContent: 'space-between'
      }}
      onSubmit={e => {
        e.preventDefault()
        e.stopPropagation()
      }}>
      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', rowGap: 18 }}>
        {
          !createdInvite && <>
            <div style={{ fontSize: 14 }}>
              <TextInputWithLabel
                label='Email address'
                placeholder='Enter email address'
                disabled={props.isEditing || isSubmitting}
                required
                value={props.userForm.emailAddress}
                onChange={e => {
                  const el = (e.target as HTMLInputElement)
                  const value = el.value

                  props.onUserFormUpdate({ emailAddress: value })
                }}
              />
            </div>
            <div style={{ fontSize: 14 }}>
              <InputLabel style={{ marginBottom: 10 }}>Roles</InputLabel>
              <RoleSelector
                onCloseFn={handleClose}
                disabled={isSubmitting}
                roleCreationPage='/settings?section=roles'
                roles={apiRoles}
                formatFunction={r => r.name}
                selectedRoles={props.userForm.roles}
                onSelectRole={r => props.onUserFormUpdate({
                  roles: [...props.userForm.roles, r]
                })}
                onRemoveRole={roleId => props.onUserFormUpdate({
                  roles: props.userForm.roles.filter(r => r !== roleId)
                })}
              />
            </div>
          </>
        }
        {
          createdInvite && <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                Share the invite link
              </span>
              <span style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST, lineHeight: '1.4' }}>
                An invitation has been successfully sent to <strong>{createdInvite.emailAddress}</strong>.
                You can also copy the invite link below to share it directly.
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <InputLabel style={{ fontSize: 14 }}>Invite link</InputLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <TextInput value={createdInvite.inviteUrl} disabled style={{ flexGrow: 1 }} />
                <IconButton
                  type='button'
                  style={{ padding: 0 }}>
                  <Copy style={{ width: 20 }} onClick={() => {
                    navigator.clipboard.writeText(createdInvite.inviteUrl ?? '')
                    infoToast('Invite link copied to clipboard')
                  }} />
                </IconButton>
              </div>
            </div>
          </>
        }
      </div>
      <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid #edf1f7' }}>
        <PrimaryButton
          style={{ width: '100%' }}
          disabled={!createdInvite && isSubmitting}
          isLoading={isSubmitting}
          onClick={async () => {
            if (createdInvite) {
              handleClose()
            } else {
              await onSubmitUser()
            }
          }}>
          { props.isEditing ? 'Update' : createdInvite ? 'Close' : 'Send invite' }
        </PrimaryButton>
      </div>
    </form>
  </Modal>
})

export default UserInviteModal
